import { OrdersListElementType, OrdersListMapType, OrdersSearchParamType } from "../interface";
import useGoToNavigation from "../../../../../util/useGotoNavigation";
import { useModal } from "../../../../../hooks";
import { Dispatch, SetStateAction, useRef } from "react";
import EmptyTableRow from "../../../../common/EmptyTableRow";
import { PaginationMain } from "../../../../../components/common/pagination/PaginationMain";
import { useUpdateOrderProgress } from "../hooks/useUpdateOrderProgress";
import { useDeleteShipment } from "../hooks/useDeleteShipment";
import { useDownloadShipmentFile } from "../hooks/useDownloadShipmentFile"
import DisplayNumber from "../../../../../components/common/number/DisplayNumber";
import AuthorityChecker from "../../../../../components/common/authorityGuard/AuthorityChecker";
import { useQueryClient } from "@tanstack/react-query";
import { usePostOrderShipmentExportShipmentInvoiceExcel } from "../hooks/usePostOrderShipmentExportShipmentInvoiceExcel";
import { usePostOrderShipmentUploadInvoice } from "../hooks/usePostOrderShipmentUploadInvoice";

interface PropsType {
    orders: OrdersListMapType,
    searchParam: OrdersSearchParamType
    checkedElements: OrdersListElementType[]
    setCheckedElements: Dispatch<SetStateAction<OrdersListElementType[]>>
}

export default function List({ orders, searchParam, checkedElements, setCheckedElements }: PropsType) {
    const qc = useQueryClient()
    const { goToNavigation } = useGoToNavigation();
    const { handleModal } = useModal();
    const { mutate: downloadShipment } = useDownloadShipmentFile();
    const { mutate: postOrderShipmentExportShipmentInvoiceExcel } = usePostOrderShipmentExportShipmentInvoiceExcel()
    const { mutate: postOrderShipmentUploadInvoice } = usePostOrderShipmentUploadInvoice();

    const ordersList = orders?.list ?? []
    const ordersCount = orders?.totalCount ?? 0

    const isAllCheck = ordersList.length !== 0 && ordersList
        .map(el => el.newShipmentId)
        .every(el => checkedElements.map(el => el.newShipmentId).includes(el));


    const closeCallbackU = () => {
        setCheckedElements([])
        qc.invalidateQueries({ queryKey: ["readShipmentList", searchParam] })
    }
    const { mutate: mutateUpdateOrderProgress } = useUpdateOrderProgress({ closeCallback: closeCallbackU })

    const closeCallbackD = () => setCheckedElements([])
    const { mutate: mutateDeleteShipment } = useDeleteShipment({ closeCallback: closeCallbackD })

    const clickAllCheck = () => {
        const ordersIdList = orders.list;

        if (isAllCheck) {
            const list = checkedElements
                .filter(el => !ordersIdList.map(el2 => el2.newShipmentId).includes(el.newShipmentId))

            setCheckedElements(list)
        } else {
            // @ts-ignore
            const list = [...new Set([...checkedElements, ...ordersIdList])]

            setCheckedElements(list)
        }
    }

    const clickCheck = (el: OrdersListElementType) => {
        if (checkedElements.map(el2 => el2.newShipmentId).includes(el.newShipmentId)) {
            const list = checkedElements.filter(el2 => el.newShipmentId !== el2.newShipmentId)

            setCheckedElements(list)
        } else {
            setCheckedElements([...checkedElements, el])
        }
    }
    /* 내부수정: 새창으로 열리기
    const clickRow = (el: OrdersListElementType) => {
        goToNavigation(`order/orders/detail/${el.newShipmentId}`, {}, false)
    } */
    const clickRow = (el: OrdersListElementType, e: React.MouseEvent) => {
        if (!el.newShipmentId) return;
        const url = `order/orders/detail/${el.newShipmentId}`;
        // 휠 클릭
        if (e.button === 1) {
            goToNavigation(url, {}, false, { newTab: true });
            return;
        }
        // ctrl / cmd
        if (e.ctrlKey || e.metaKey) {
            goToNavigation(url, {}, false, { newTab: true });
            return;
        }
        // 일반 클릭
        if (e.button === 0) {
            goToNavigation(url, {}, false);
        }
    };

    const btnDelete = () => {
        if (checkedElements.length === 0) {
            handleModal({
                isOpen: true,
                contents: "삭제할 항목을 선택해주세요.",
                type: "alert",
            });
        } else if (checkedElements.some(el => el.newPProgress === 100000002 || el.newPProgress === 100000003)) {
            handleModal({
                isOpen: true,
                contents: "출하 혹은 이관 상태인 항목이 있어 삭제할 수 없습니다.",
                type: "alert",
            });
        } else {
            handleModal({
                isOpen: true,
                contents: "삭제하시겠습니까?",
                type: "confirm",
                callback: () => {
                    mutateDeleteShipment(checkedElements.map(el => ({ id: el.newShipmentId })))
                }
            });
        }
    }

    // TODO: 수주/출하 목록 엑셀 내보내기
    const btnExportExcel = () => {
        downloadShipment(searchParam);
    }

    // 송장 내보내기
    const handleExportInvoice = () => {
        if (checkedElements.length === 0) {
            handleModal({
                isOpen: true,
                contents: "송장 내보내기할 항목을 선택해주세요.",
                type: "alert",
            });
            return;
        }

        handleModal({
            isOpen: true,
            contents: "선택한 항목의 송장을 내보내시겠습니까?",
            type: "confirm",
            callback: () => {
                console.log("송장 내보내기 list : ", checkedElements);
                postOrderShipmentExportShipmentInvoiceExcel(checkedElements);
                setCheckedElements([]);
            }
        })
    }

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // 송장 가져오기
    const handleImportInvoice = () => {
        fileInputRef.current?.click();
    }

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        console.log("선택한 파일: ", file);
        handleModal({
            isOpen: true,
            contents: "선택한 파일을 업로드 하시겠습니까?",
            type: "confirm",
            callback: () => {
                postOrderShipmentUploadInvoice(file);
                e.target.value = ""; // 같은 파일 다시 업로드할 수 있도록 초기화
            },
        });
    }

    const btnChangeProgress = (key: "shipmentRequest" // 출하요청
        | "shipmentRequestCancel" // 출하요청 취소
        | "shipment" // 출하
        | "shipmentCancel" // 출하 취소
        | "transfer" // 이관
        | "transferCancel" // 이관 취소
    ) => {
        let msg = "";
        let newProgress = 0;

        switch (key) {
            case "shipmentRequest":
                msg = "출하요청 하시겠습니까?"
                newProgress = 100000001
                break;
            case "shipmentRequestCancel":
                msg = "출하요청을 취소하시겠습니까?"
                newProgress = 100000000
                break;
            case "shipment":
                msg = "출하 하시겠습니까?"
                newProgress = 100000002
                break;
            case "shipmentCancel":
                msg = "출하 취소하시겠습니까?"
                newProgress = 100000001
                break;
            case "transfer":
                msg = "이관 하시겠습니까?"
                newProgress = 100000003
                break;
            case "transferCancel":
                msg = "이관 취소하시겠습니까?"
                newProgress = 100000002
                break;
        }

        handleModal({
            isOpen: true,
            contents: msg,
            type: "confirm",
            callback: () => mutateUpdateOrderProgress(checkedElements.map(el => ({
                newShipmentId: el.newShipmentId,
                progress: newProgress
            })))
        });
    }

    // 100000000.수주, 100000001.출하요청, 100000002.출하, 100000003.이관
    const isBtnShipmentRequestDisabled = checkedElements.length === 0
        || checkedElements.some(el => el.newPProgress !== 100000000) // 수주가 아닌게 하나라도 있으면 비활성화

    const isBtnShipmentRequestCancelDisabled = checkedElements.length === 0
        || !checkedElements.every(el => el.newPProgress === 100000001) // 모두 출하요청이 아니면 비활성화

    const isBtnShipmentDisabled = checkedElements.length === 0
        || checkedElements.some(el => el.newPProgress !== 100000001) // 출하요청이 아닌게 하나라도 있으면 비활성화

    const isBtnShipmentCancelDisabled = checkedElements.length === 0
        || !checkedElements.every(el => el.newPProgress === 100000002) // 모두 출하가 아니면 비활성화

    const isBtnTransferDisabled = checkedElements.length === 0
        || checkedElements.some(el => el.newPProgress !== 100000002) // 출하가 아닌게 하나라도 있으면 비활성화

    const isBtnTransferCancelDisabled = checkedElements.length === 0
        || !checkedElements.every(el => el.newPProgress === 100000003) // 모두 이관이 아니면 비활성화


    return <div className="content_wrap">
        <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx"
            style={{ display: "none" }}
            onChange={onFileChange}
        />

        <div className="table_top">
            <h6 className="table_stats">수주 목록(<span>{ordersCount}</span>건)</h6>
            <div className="table_toolbar">

                <button className="btn btn-primary w-150px px-0" onClick={handleExportInvoice}>
                    <img src={process.env.PUBLIC_URL + "/assets/images/icon_export.png"} alt="" />송장 내보내기
                </button>
                <button className="btn btn-info w-150px px-0" onClick={handleImportInvoice}>
                    <img src={process.env.PUBLIC_URL + "/assets/images/icon_export.png"} alt="" />송장 가져오기
                </button>
                <button className="btn btn-dark w-150px px-0" onClick={btnExportExcel}>
                    <img src={process.env.PUBLIC_URL + "/assets/images/icon_export.png"} alt="" />엑셀 내보내기
                </button>

                <AuthorityChecker authorityNames={['shipmentWriteYn']}>
                    <button className="btn btn-success w-80px px-0 ms-40px"
                        onClick={() => btnChangeProgress("shipmentRequest")}
                        disabled={isBtnShipmentRequestDisabled}
                    >출하요청
                    </button>
                    <button className="btn btn-outline-success px-0"
                        onClick={() => btnChangeProgress("shipmentRequestCancel")}
                        disabled={isBtnShipmentRequestCancelDisabled}
                    >출하요청 취소
                    </button>
                    <button className="btn btn-primary w-80px px-0"
                        onClick={() => btnChangeProgress("shipment")}
                        disabled={isBtnShipmentDisabled}
                    >출하
                    </button>
                    {/*내부수정: 출하취소-이관 위치 변경*/}
                    <button className="btn btn-outline-primary w-80px px-0"
                        onClick={() => btnChangeProgress("shipmentCancel")}
                        disabled={isBtnTransferDisabled}
                    >출하취소
                    </button>
                    <button className="btn btn-info w-80px px-0"
                        onClick={() => btnChangeProgress("transfer")}
                        disabled={isBtnShipmentCancelDisabled}
                    >이관
                    </button>
                    <button className="btn btn-outline-info w-80px px-0"
                        onClick={() => btnChangeProgress("transferCancel")}
                        disabled={isBtnTransferCancelDisabled}
                    >이관취소
                    </button>
                    <button className="btn btn-outline-dark" onClick={btnDelete}>
                        <img src={process.env.PUBLIC_URL + "/assets/images/icon_cross.png"} alt="" />선택삭제
                    </button>
                </AuthorityChecker>
            </div>
        </div>

        {/* 내부수정: table_scroll 삭제 */}
        <table className="table table-bordered my_table">
            <thead>
                <tr>
                    <AuthorityChecker authorityNames={['shipmentWriteYn']}>
                        <th style={{ width: "3%" }}>
                            <div className="checkbox_outer">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={isAllCheck}
                                    onClick={clickAllCheck}
                                />
                            </div>
                        </th>
                    </AuthorityChecker>
                    {/* 내부수정: 컬럼 사이즈 조절 */}
                    <th style={{ width: "7%" }}>수주일자</th>
                    <th style={{ width: "7%" }}>출하일자</th>
                    <th style={{ width: "11%" }}>수주유형</th>
                    <th style={{ width: "5%" }}>담당자</th>
                    <th>고객사</th>
                    <th>매출처</th>
                    <th style={{ width: "4%" }}>환종</th>
                    <th style={{ width: "8%" }}>금액</th>
                    <th style={{ width: "5%" }}>상태</th>
                    <th style={{ width: "5%" }}>발송방법</th>
                    <th style={{ width: "10%" }}>ERP 수주계약 코드</th>
                    {/* <th style={{width: "9%"}}>마감일자</th>* */}
                </tr>
            </thead>
            <tbody>
                {ordersList.length !== 0 ?
                    ordersList.map(el => <tr
                        className={checkedElements.map(el2 => el2.newShipmentId).includes(el.newShipmentId) ? "clickable lightblue" : "clickable"}
                        onMouseDown={(e) => clickRow(el, e)}>
                        <AuthorityChecker authorityNames={['shipmentWriteYn']}>
                            <td className="text-center" onMouseDown={e => e.stopPropagation()}>
                                <div className="checkbox_outer">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={checkedElements.map(el2 => el2.newShipmentId).includes(el.newShipmentId)}
                                        onClick={() => clickCheck(el)}
                                    />
                                </div>
                            </td>
                        </AuthorityChecker>
                        <td className="text-center">{el.newDtOrder}</td>
                        <td className="text-center">{el.newDtShipment}</td>
                        <td className="text-center">{el.orderTypeName}</td>
                        <td className="text-center">{el.owningUserName}</td>
                        <td className="text-center">{el.enduserName}</td>
                        <td className="text-center">{el.customerName}</td>
                        <td className="text-center">{el.currencyName}</td>
                        {/* 내부수정: KRW/USD에 따라 원화금액/외화금액 분기 표시 */}
                        <td className="text-center">
                            <DisplayNumber value={el.newPCurrency === 100000000 ? el.newDAmount : el.newDAmountForeign} />
                        </td>
                        {/* <td className="text-center"><DisplayNumber value={el.newDAmount} /></td> */}
                        <td className="text-center">{el.progressName}</td>
                        <td className="text-center">{el.newPShipmentName}</td>
                        <td className="text-center">{el.newTxtProjectCode}</td>
                        {/* <td className="text-center">{el.endDt}</td>* */}
                    </tr>)
                    : <EmptyTableRow colSpan={12} />
                }
            </tbody>
        </table>

        <PaginationMain totalCount={ordersCount} />
    </div>
}